import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { DashboardBasicData } from '../../../core/models/dashboard/DashboardBasicData.model';
import { DashboardApplication } from '../../../core/models/dashboard/DashboardApplication.model';
import { UserDashboardDataModel } from '../../../core/models/dashboard/UserDashboardData.model';
import { DashboardService } from '../../../core/services/dashboard.service';
import {
    DASHBOARD_APPLICATION_CATALOG,
    DASHBOARD_PROJECT_CATALOG,
    DASHBOARD_ANNOUNCEMENT_CATALOG
} from './dashboard-ui.catalog';
import { Router } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { ProjectService } from '../../../core/services/project.service';

@Component({
    selector: 'app-user-dashboard',
    standalone: false,
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
    DashboardData: UserDashboardDataModel = this.CreateEmptyDashboard();
    dashboardErrorMessage: string = '';

    private dashboardSubscription?: Subscription;

    readonly applicationCatalog = DASHBOARD_APPLICATION_CATALOG;
    readonly projectCatalog = DASHBOARD_PROJECT_CATALOG;
    readonly announcementCatalog = DASHBOARD_ANNOUNCEMENT_CATALOG;
    readonly statCodes = [1, 2, 3];
    readonly usageCodes = [1, 2, 3];

    private readonly numberFormatter = new Intl.NumberFormat('tr-TR', {
        maximumFractionDigits: 2
    });

    private readonly dateFormatter = new Intl.DateTimeFormat('tr-TR', {
        timeZone: 'Europe/Istanbul',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });

    private readonly shortDateFormatter = new Intl.DateTimeFormat('tr-TR', {
        timeZone: 'Europe/Istanbul',
        day: 'numeric',
        month: 'short'
    });

    private readonly relativeFormatter = new Intl.RelativeTimeFormat('tr-TR', {
        numeric: 'always'
    });

    constructor(
        private readonly dashboardService: DashboardService,private router: Router, projectSvc:ProjectService, private auth:AuthService  ,private readonly changeDetectorRef: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.LoadDashboard();
    }

    GoToLogin(): void {
    this.auth.logout();
    this.router.navigateByUrl('/login');
    }

    LoadDashboard(): void {
        this.dashboardSubscription?.unsubscribe();
        this.DashboardData = this.CreateEmptyDashboard();
        this.dashboardErrorMessage = '';

        this.dashboardSubscription = this.dashboardService.GetDashboard().subscribe({
            next: (response) => {
                if (!response || response.isError || !response.data) {
                    this.ShowLoadingError(
                        'Çalışma alanı bilgileriniz yüklenemedi. Lütfen tekrar giriş yaparak yeniden deneyin.'
                    );
                    return;
                }

                this.DashboardData = this.NormalizeDashboard(response.data);
                this.dashboardErrorMessage = '';
                this.changeDetectorRef.markForCheck();
            },
            error: () => {
                this.ShowLoadingError(
                    'Çalışma alanına erişilirken bir sorun oluştu. Lütfen tekrar giriş yaparak yeniden deneyin.'
                );
            }
        });

        this.changeDetectorRef.markForCheck();
    }

    private ShowLoadingError(message: string): void {
        this.DashboardData = this.CreateEmptyDashboard();
        this.dashboardErrorMessage = message;
        this.changeDetectorRef.markForCheck();
    }

    ngOnDestroy(): void {
        this.dashboardSubscription?.unsubscribe();
    }

    private CreateEmptyBasic(): DashboardBasicData {
        return {
            lastIP: '',
            lastLoginDate: '',
            lastAgent: '',
            userId: '',
            userEmail: '',
            helloName: '',
            fullName: '',
            packetName: '',
            isSubscriptionActive: false,
            subscriptionBeginDate: '',
            subscriptionEndDate: '',
            activeTeamMemeberNumber: 0,
            maxTeamNumber: 0,
            totalProjects: 0,
            totalActiveApplications: 0,
            serviceUptime: 0,
            maxProjectLimitsNumber: 0,
            currentProjectNumber: 0,
            currentUsebleProjectNumber: 0,
            maxMonthlyUsableNumber: 0,
            currentMonthlyUsableNumber: 0,
            maxApplicationLimitsNumber:0,
            currentApplicationNumber:0,
            currentUsebleApplicationNumber:0
        };
    }

    private CreateEmptyDashboard(): UserDashboardDataModel {
        return {
            basic: this.CreateEmptyBasic(),
            stats: [],
            applications: [],
            projects: [],
            featuredApplications: [],
            marketplaceHighlights: [],
            usages: [],
            weeklyUsage: [],
            announcements: [],
            serviceStatus: {
                activeCount: 0,
                totalCount: 0
            }
        };
    }

    private NormalizeBasic(
        basic: Partial<DashboardBasicData> | null | undefined
    ): DashboardBasicData {
        return {
            lastIP: this.Text(basic?.lastIP),
            lastLoginDate: this.Text(basic?.lastLoginDate),
            lastAgent: this.Text(basic?.lastAgent),
            userId: this.Text(basic?.userId),
            userEmail: this.Text(basic?.userEmail),
            helloName: this.Text(basic?.helloName),
            fullName: this.Text(basic?.fullName),
            packetName: this.Text(basic?.packetName),
            isSubscriptionActive: basic?.isSubscriptionActive === true,
            subscriptionBeginDate: this.Text(basic?.subscriptionBeginDate),
            subscriptionEndDate: this.Text(basic?.subscriptionEndDate),
            activeTeamMemeberNumber: this.Number(basic?.activeTeamMemeberNumber),
            maxTeamNumber: this.Number(basic?.maxTeamNumber),
            totalProjects: this.Number(basic?.totalProjects),
            totalActiveApplications: this.Number(basic?.totalActiveApplications),
            serviceUptime: this.Number(basic?.serviceUptime),
            maxProjectLimitsNumber: this.Number(basic?.maxProjectLimitsNumber),
            currentProjectNumber: this.Number(basic?.currentProjectNumber),
            currentUsebleProjectNumber: this.Number(basic?.currentUsebleProjectNumber),
            maxMonthlyUsableNumber: this.Number(basic?.maxMonthlyUsableNumber),
            currentMonthlyUsableNumber: this.Number(basic?.currentMonthlyUsableNumber),
            maxApplicationLimitsNumber:this.Number(basic?.maxApplicationLimitsNumber),
            currentApplicationNumber:this.Number(basic?.currentApplicationNumber),
            currentUsebleApplicationNumber:this.Number(basic?.currentUsebleApplicationNumber)
        };
    }

    private NormalizeDashboard(data: Partial<UserDashboardDataModel>): UserDashboardDataModel {
        return {
            basic: this.NormalizeBasic(data.basic),
            stats: this.List(data.stats).map((item) => ({
                code: this.Number(item.code),
                value: this.Number(item.value),
                activeCount: this.Number(item.activeCount),
                completedCount: this.Number(item.completedCount),
                highPriorityCount: this.Number(item.highPriorityCount),
                totalCount: this.Number(item.totalCount),
                trendPercent: this.Number(item.trendPercent),
                trendDown: item.trendDown === true
            })),
            applications: this.List(data.applications).map((item) => ({
                id: this.Number(item.id),
                typeCode: this.Number(item.typeCode)
            })),
            projects: this.List(data.projects).map((item) => ({
                id: this.Number(item.id),
                applicationId: this.Number(item.applicationId),
                statusCode: this.Number(item.statusCode),
                updatedAtUnix: this.Unix(item.updatedAtUnix)
            })),
            featuredApplications: this.List(data.featuredApplications).map((item) => ({
                applicationId: this.Number(item.applicationId)
            })),
            marketplaceHighlights: this.List(data.marketplaceHighlights).map((item) => ({
                applicationId: this.Number(item.applicationId)
            })),
            usages: this.List(data.usages).map((item) => ({
                code: this.Number(item.code),
                current: this.Number(item.current),
                limit: this.Number(item.limit)
            })),
            weeklyUsage: this.List(data.weeklyUsage)
                .map((item) => ({
                    dateUnix: this.Unix(item.dateUnix),
                    value: this.Number(item.value)
                }))
                .sort((left, right) => left.dateUnix - right.dateUnix),
            announcements: this.List(data.announcements).map((item) => ({
                id: this.Number(item.id),
                publishedAtUnix: this.Unix(item.publishedAtUnix)
            })),
            serviceStatus: {
                activeCount: this.Number(data.serviceStatus?.activeCount),
                totalCount: this.Number(data.serviceStatus?.totalCount)
            }
        };
    }

    private List<T>(value: T[] | null | undefined): T[] {
        if (!Array.isArray(value)) {
            return [];
        }

        return value.filter((item) => item !== null && item !== undefined);
    }

    private Text(value: unknown): string {
        return typeof value === 'string' ? value : '';
    }

    Number(value: unknown): number {
        return typeof value === 'number' && Number.isFinite(value) && value >= 0
            ? value
            : 0;
    }

    private Unix(value: unknown): number {
        const unix = this.Number(value);

        return unix > 0 && unix <= 8640000000000 ? Math.floor(unix) : 0;
    }

    FormatNumber(value: unknown): string {
        return this.numberFormatter.format(this.Number(value));
    }

    Display(value: string | null | undefined): string {
        return typeof value === 'string' && value.trim() ? value : '0';
    }

    Stat(code: number): UserDashboardDataModel['stats'][number] {
        return this.DashboardData.stats.find((item) => item.code === code) ?? {
            code,
            value: 0,
            activeCount: 0,
            completedCount: 0,
            highPriorityCount: 0,
            totalCount: 0,
            trendPercent: 0,
            trendDown: false
        };
    }

    Usage(code: number): UserDashboardDataModel['usages'][number] {
        return this.DashboardData.usages.find((item) => item.code === code) ?? {
            code,
            current: 0,
            limit: 0
        };
    }

    UsagePercent(code: number): number {
        const usage = this.Usage(code);

        return usage.limit > 0 ? Math.min(100, usage.current * 100 / usage.limit) : 0;
    }

    FormatDate(value: unknown): string {
        const unix = this.Unix(value);

        return unix > 0 ? this.dateFormatter.format(new Date(unix * 1000)) : '0';
    }

    FormatShortDate(value: unknown): string {
        const unix = this.Unix(value);

        return unix > 0 ? this.shortDateFormatter.format(new Date(unix * 1000)) : '0';
    }

    RelativeDate(value: unknown): string {
        const unix = this.Unix(value);

        if (unix === 0) {
            return '0';
        }

        const seconds = (unix * 1000 - Date.now()) / 1000;
        const absoluteSeconds = Math.abs(seconds);
        let unit: Intl.RelativeTimeFormatUnit = 'minute';
        let divisor = 60;

        if (absoluteSeconds >= 604800) {
            unit = 'week';
            divisor = 604800;
        } else if (absoluteSeconds >= 86400) {
            unit = 'day';
            divisor = 86400;
        } else if (absoluteSeconds >= 3600) {
            unit = 'hour';
            divisor = 3600;
        }

        return this.relativeFormatter.format(Math.trunc(seconds / divisor), unit);
    }

    get chartMaximum(): number {
        return this.DashboardData.weeklyUsage.reduce(
            (maximum, item) => Math.max(maximum, item.value),
            0
        );
    }

    get highlightedDayIndex(): number {
        const maximum = this.chartMaximum;

        return maximum > 0
            ? this.DashboardData.weeklyUsage.findIndex((item) => item.value === maximum)
            : -1;
    }

    ChartHeight(value: number): number {
        return this.chartMaximum > 0 ? this.Number(value) * 90 / this.chartMaximum : 0;
    }

    get allServicesActive(): boolean {
        const status = this.DashboardData.serviceStatus;

        return status.totalCount > 0 && status.activeCount === status.totalCount;
    }

    ApplicationName(id: number): string {
        return this.Display(this.applicationCatalog[id]?.name);
    }

    ApplicationIcon(id: number): string {
        return this.applicationCatalog[id]?.icon ?? 'bi bi-grid';
    }

    ApplicationTheme(id: number): string {
        return this.applicationCatalog[id]?.theme ?? 'navy';
    }

    ProjectName(id: number): string {
        return this.Display(this.projectCatalog[id]?.name);
    }

    ProjectDescription(id: number): string {
        return this.Display(this.projectCatalog[id]?.description);
    }

    ProjectImage(id: number): string | null {
        return this.projectCatalog[id]?.image ?? null;
    }

    FeaturedBackground(id: number): string | null {
        const image = this.applicationCatalog[id]?.image;

        return image ? 'url("' + image + '")' : null;
    }

    ApplicationTags(id: number): string[] {
        return this.applicationCatalog[id]?.tags ?? [];
    }

    NewsTitle(id: number): string {
        return this.Display(this.announcementCatalog[id]?.title);
    }

    NewsDescription(id: number): string {
        return this.Display(this.announcementCatalog[id]?.description);
    }

    NewsImage(id: number): string | null {
        return this.announcementCatalog[id]?.image ?? null;
    }

    get accountName(): string {
        return this.Display(this.DashboardData.basic.fullName);
    }

    get accountEmail(): string {
        return this.Display(this.DashboardData.basic.userEmail);
    }

    get accountAvatar(): string | null {
        // Mevcut DashboardBasicData modelinde avatar alanı bulunmuyor.
        return null;
    }

    get packageName(): string {
        return this.Display(this.DashboardData.basic.packetName);
    }

    openApplication(app: DashboardApplication): void {
        this.OpenCatalogApplication(app.id);
    }

    OpenCatalogApplication(id: number): void {
        const route = this.applicationCatalog[id]?.route;

        if (route) {
            window.location.href = route;
        }
    }

    get MonthlyUsagePercent(): number {
    const current = this.DashboardData?.basic?.currentMonthlyUsableNumber ?? 0;
    const max = this.DashboardData?.basic?.maxMonthlyUsableNumber ?? 0;

    if (max <= 0) {
        return 0;
    }

    return Math.max(0, Math.min(100, (current / max) * 100));
}
}
